import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, Mail } from "lucide-react";
import { toast } from "sonner";

export const TEMPLATE_VARS = ["name", "first_name", "phone", "email", "project", "configuration", "agent_name", "agent_phone", "agent_email", "company"];
const inputCls = "w-full border border-[#E6E4DD] rounded-sm px-3 text-sm focus:outline-none focus:border-forest";
const triggerCls = "h-10 rounded-sm border border-[#E6E4DD] text-sm text-forest hover:border-forest transition-colors duration-150 inline-flex items-center justify-center gap-2 flex-1";

export const VarChips = ({ onPick }) => (
  <div className="flex flex-wrap gap-1" data-testid="template-var-chips">
    {TEMPLATE_VARS.map((v) => (
      <button key={v} type="button" onClick={() => onPick(`{${v}}`)} className="text-[10px] font-mono rounded-sm border border-[#E6E4DD] px-1.5 py-0.5 text-forest/70 hover:border-forest hover:text-forest">{`{${v}}`}</button>
    ))}
  </div>
);

const TemplatePicker = ({ channel, onPick }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/message-templates", { params: { channel } }).then((r) => setItems(r.data)).catch(() => {}); }, [channel]);
  if (!items.length) return <div className="text-xs text-forest/50">No saved {channel} templates yet — admins and managers can add them under Email & SMS Templates.</div>;
  return (
    <Select onValueChange={(id) => onPick(items.find((t) => t.id === id))}>
      <SelectTrigger data-testid={`lead-${channel}-template-select`} className="h-10 rounded-sm border-[#E6E4DD]"><SelectValue placeholder="Use a saved template" /></SelectTrigger>
      <SelectContent>{items.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
    </Select>
  );
};

export function SendEmailDialog({ lead, onSent }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [tid, setTid] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/send-email`, { subject, body, template_id: tid });
      toast.success(`Email sent to ${lead.email}`, { description: data.fallback ? `Your sender address isn't verified yet, so it went from ${data.from}` : `From ${data.from}` });
      setOpen(false); setSubject(""); setBody(""); setTid(null); onSent?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="lead-send-email-btn" className={triggerCls}><Mail className="h-4 w-4" /> Send email</button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Email {lead.name}</DialogTitle></DialogHeader>
        {!lead.email ? <div data-testid="lead-email-missing" className="text-sm text-clay">This lead has no email address. Add one with Edit lead first.</div> : (
          <div className="space-y-3">
            <div className="text-xs text-forest/60">To: {lead.email}</div>
            <TemplatePicker channel="email" onPick={(t) => { setTid(t.id); setSubject(t.subject || ""); setBody(t.body); }} />
            <input data-testid="lead-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className={`${inputCls} h-10`} />
            <textarea data-testid="lead-email-body" value={body} onChange={(e) => setBody(e.target.value)} rows={7} placeholder="Write your message…" className={`${inputCls} py-2`} />
            <VarChips onPick={(v) => setBody((b) => b + v)} />
            <div className="text-[11px] text-forest/50">Fields like {"{name}"} are filled in automatically when sent.</div>
          </div>
        )}
        <DialogFooter>
          <button data-testid="lead-email-send" onClick={send} disabled={busy || !lead.email || !subject.trim() || !body.trim()} className="h-9 px-4 rounded-sm bg-forest text-white text-sm font-medium hover:bg-forest-soft transition-colors duration-150 disabled:opacity-50">{busy ? "Sending…" : "Send email"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SendSmsDialog({ lead, onSent }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [tid, setTid] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/send-sms`, { text, template_id: tid });
      window.location.href = data.sms_uri;
      toast.success("Opening your SMS app — the message is logged on this lead");
      setOpen(false); setText(""); setTid(null); onSent?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="lead-send-sms-btn" className={triggerCls}><MessageSquare className="h-4 w-4" /> Send SMS</button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-md">
        <DialogHeader><DialogTitle className="font-display text-2xl">SMS {lead.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <TemplatePicker channel="sms" onPick={(t) => { setTid(t.id); setText(t.body); }} />
          <textarea data-testid="lead-sms-text" value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Type your SMS…" className={`${inputCls} py-2`} />
          <div className="flex items-center justify-between text-[11px] text-forest/50"><VarChips onPick={(v) => setText((t) => t + v)} /></div>
          <div className="text-[11px] text-forest/50">{text.length} characters · opens your phone's SMS app with the message ready (direct sending via CallerDesk coming soon).</div>
        </div>
        <DialogFooter>
          <button data-testid="lead-sms-send" onClick={send} disabled={busy || !text.trim()} className="h-9 px-4 rounded-sm bg-forest text-white text-sm font-medium hover:bg-forest-soft transition-colors duration-150 disabled:opacity-50">{busy ? "Opening…" : "Send SMS"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
